// Resume-last-scope transport. The deepest breadcrumb trail is persisted per-actor on the
// server (account_profile.last_scope) — DB-backed per the VW state policy (no client storage) —
// so it survives a page refresh. Best-effort: failures are non-fatal (the URL is canonical).

import { isLocalDevMockAuth } from "../../lib/auth/localDevMock";

const API_BASE = "/api/workspace/profile";
const FETCH_OPTS: RequestInit = { credentials: "include" };

/** Read the stored last-scope JSON string (null if none / unauthenticated). */
export async function getLastScope(): Promise<string | null> {
  if (isLocalDevMockAuth()) return null;
  try {
    const res = await fetch(API_BASE, FETCH_OPTS);
    if (!res.ok) return null;
    const data = (await res.json()) as { last_scope?: string | null };
    return data.last_scope ?? null;
  } catch {
    return null; // network/parse failure → no resume
  }
}

/** Account-profile fields the client may upsert (the BE PUT merges — omit a field to leave it). */
export interface ProfilePatch {
  theme_preference?: string;
  last_scope?: string;
}

/** The single account WRITE path — upserts one or more account_profile fields (best-effort; failures are
 *  non-fatal). Backs both the preferences layer's ACCOUNT scope and the resume-last-scope transport. */
export async function patchProfile(patch: ProfilePatch): Promise<void> {
  // Local mock mode is intentionally DB-less. The theme setter has already updated the canonical
  // design-system cookie before this best-effort account write is requested.
  if (isLocalDevMockAuth()) return;
  try {
    await fetch(API_BASE, {
      ...FETCH_OPTS,
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
  } catch {
    // best-effort: persistence is a comfort layer, not correctness
  }
}

/** Persist the last-scope JSON string (best-effort). Thin wrapper over the single write path. */
export async function putLastScope(lastScope: string): Promise<void> {
  await patchProfile({ last_scope: lastScope });
}
